/**
 * Source identification: PDF-ingested vs web-scraped financial data.
 *
 * A company came from PDF ingestion iff EITHER
 *   - its companyId starts with "ind_pdf_" (India), "uk_pdf_" (UK) or
 *     "comp_us_" (US PDF extraction), OR
 *   - a FinancialReport row has source == "pdf_extract"
 *     (PDF report ids also start with "pdfrep_").
 *
 * Scraped India ids look like "ind_DABUR" with report ids like
 * "ind_annual_DABUR_2024", and scraped SEC ids are "comp_<numeric CIK>" — so
 * the PDF prefixes MUST be tested before any plain "ind_"/"uk_"/"comp_"
 * prefix heuristic elsewhere ("comp_us_" never collides with a numeric CIK).
 *
 * Note a company can hold BOTH scraped and PDF reports for the same fiscal
 * year, which is why report rows carry their own per-row check rather than
 * inheriting the company-level verdict.
 */

const PDF_COMPANY_ID_PREFIXES = ['ind_pdf_', 'uk_pdf_', 'us_pdf_', 'comp_us_'];
const PDF_REPORT_ID_PREFIX = 'pdfrep_';
export const PDF_REPORT_SOURCE = 'pdf_extract';

export function isPdfCompanyId(companyId: string | null | undefined): boolean {
  if (!companyId) return false;
  const id = companyId.toLowerCase();
  return PDF_COMPANY_ID_PREFIXES.some((prefix) => id.startsWith(prefix));
}

/** Per-report check. Accepts both snake_case and camelCase report id keys. */
export function isPdfReport(report: {
  report_id?: string | null;
  reportId?: string | null;
  source?: string | null;
}): boolean {
  if ((report.source ?? '').toLowerCase() === PDF_REPORT_SOURCE) return true;
  const id = (report.report_id ?? report.reportId ?? '').toLowerCase();
  return id.startsWith(PDF_REPORT_ID_PREFIX);
}

/** Company-level check: id prefix, company source, or any PDF-sourced report. */
export function isPdfCompany(
  companyId: string | null | undefined,
  companySource?: string | null,
  reports?: Array<{ report_id?: string | null; reportId?: string | null; source?: string | null }>
): boolean {
  if (isPdfCompanyId(companyId)) return true;
  if ((companySource ?? '').toLowerCase() === PDF_REPORT_SOURCE) return true;
  return (reports ?? []).some(isPdfReport);
}
